import { useQuery } from '@apollo/react-hooks';
import {
  DefaultCouponMapping,
  Product,
  ProductElement,
  ProductGroup,
  ProductPremiumProjection,
  Quotation,
  RETRIEVE_QUOTATION,
  RewardSet,
  SumAssuredRange,
} from '@bowtie-web/common';
import dayjs from 'dayjs';
import { keyBy, orderBy } from 'lodash';
import React from 'react';
import {
  DEFAULT_RANGE,
  EXTRA_PRODUCT_SPECS_BY_CODE,
} from '../constants/product';

/**
 * Similar to `usePrevious` to preserve the previous value during re-render, but
 * skipping `null` or `undefined`
 * https://github.com/apollographql/apollo-client/issues/6039#issuecomment-657703849
 * @param value - the value to be track and preserve
 * @returns the previous non-nullish `value` before re-render
 */
export const usePreviousNonNullish = <T>(value: T): T => {
  const ref = React.useRef<T>(value);
  React.useEffect(() => {
    if (value !== null && value !== undefined) {
      ref.current = value;
    }
  });
  return ref.current;
};

export const useQuotation = ({
  productId,
  age,
  gender,
  smoker,
  sumAssured,
  couponCode,
}: {
  productId: string;
  age?: number;
  gender: 'male' | 'female';
  smoker: 'smoker' | 'non-smoker';
  sumAssured?: number;
  couponCode?: string;
}) => {
  const { data, ...payload } = useQuery<{ quotation: Quotation }>(
    RETRIEVE_QUOTATION,
    {
      variables: {
        product_id: productId,
        age,
        gender,
        smoker,
        sum_assured: sumAssured,
        coupon_code: couponCode,
      },
      skip: !productId,
    },
  );

  return [data?.quotation, payload] as const;
};

export const getProductList = (productGroups: ProductGroup[]) => {
  let productList: Product[] = [];
  productGroups.forEach((productGroup: ProductGroup) => {
    productGroup.elements.forEach((element: ProductElement) => {
      productList = [element.resource, ...productList];
    });
  });
  return productList;
};

export const getProductListAttributes = (productGroups: ProductGroup[]) => {
  return productGroups[0].variables;
};

export const getElementFromGroupByVariableId = (
  elements: ProductElement[],
  id: number,
) => {
  return elements.find(el => el.variable_ids.includes(id))?.resource;
};

export const getAssuredRange = (age: number, ranges?: SumAssuredRange[]) =>
  ranges?.find(
    ({ max_issue_age, min_issue_age }) =>
      max_issue_age >= age && min_issue_age <= age,
  ) || DEFAULT_RANGE;

export const isSumAssuredRequired = (productCode: string) =>
  EXTRA_PRODUCT_SPECS_BY_CODE[productCode]?.sumAssured;

export const parseObjectValuesToNumber = <T extends object>(object: T) => {
  return Object.entries(object).reduce((acc, [key, value]) => {
    const parsedValue = parseInt(value as string, 10);

    return {
      ...acc,
      [key]: Number.isNaN(parsedValue) ? value : parsedValue,
    };
  }, {});
};

export const getEffectiveDefaultCouponMappings = (
  mappings: DefaultCouponMapping[],
) => {
  const now = new Date();

  return mappings.filter(
    ({ effective_from, effective_to }) =>
      dayjs(effective_from).isBefore(now) && dayjs(effective_to).isAfter(now),
  );
};

export const getUniqueTypeDefaultCouponMappings = (
  mappings: DefaultCouponMapping[],
) => {
  const sortedMappings = orderBy(mappings, ['effective_from'], ['asc']);

  const mappingByTypeCode = keyBy(sortedMappings, 'type.code');

  return Object.values(mappingByTypeCode);
};

export const getUniqueRewardSetForSameProduct = (
  rewardSets: (RewardSet & { coupon_code: string })[],
) => {
  const sortedRewardSet = orderBy(rewardSets, ['create_date'], ['asc']);

  const setByCouponCode = keyBy(sortedRewardSet, 'coupon_code');

  return Object.values(setByCouponCode);
};

export const getCouponSelectionToProductIdMapping = (
  productIds: string[],
  defaultCouponMapping: DefaultCouponMapping[],
  rewardSets: (RewardSet & { coupon_code: string })[],
) => {
  const defaultCouponMappingByCouponCode = keyBy(
    defaultCouponMapping,
    'coupon_code',
  );

  return productIds.reduce(
    (
      defaultCouponMapping: {
        [productId: string]: {
          couponCode: string;
          rewardSet: RewardSet;
          defaultCouponMapping: DefaultCouponMapping;
        }[];
      },
      productId,
    ) => {
      const rewardSetsForProduct = getUniqueRewardSetForSameProduct(
        rewardSets.filter(({ applicable_product_codes }) =>
          applicable_product_codes.includes(productId),
        ),
      );

      if (rewardSetsForProduct.length === 0) {
        return defaultCouponMapping;
      }

      const resultMapping = rewardSetsForProduct.map(rewardSet => ({
        rewardSet,
        couponCode: rewardSet.coupon_code,
        defaultCouponMapping:
          defaultCouponMappingByCouponCode[rewardSet.coupon_code],
      }));

      return {
        ...defaultCouponMapping,
        [productId]: resultMapping,
      };
    },
    {},
  );
};

export const getTotalPremiumFromProjections = (
  projections: ProductPremiumProjection[],
  productCode: string,
) => {
  const premiumProjectionForTargetProduct = projections.find(
    projection => projection.product.code === productCode,
  )?.projection;

  return premiumProjectionForTargetProduct?.quotations
    .map(({ basic_premium }) => basic_premium)
    .reduce((acc, curr) => acc + curr, 0);
};
